// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  DragIndicatorRounded,
  EditRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ItemField } from '@/items/api/types';
import { ItemFieldTypeLabel } from '@/items/components/item-field-type-label';
import {
  ItemFieldCardData,
  isItemFieldCardData,
} from '@/items/components/types';
import { UpdateItemFieldDialog } from '@/items/components/update-item-field-dialog';
import {
  Edge,
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, IconButton, Typography, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

/**
 * Styled container component for the item field card.
 * Provides visual feedback for drag and drop operations and hover states.
 */
const Container = styled(Box)(({ theme }) => ({
  gridColumn: 'span 4',
  display: 'grid',
  position: 'relative',
  alignItems: 'start',
  flexDirection: 'row',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  gridTemplateColumns: 'subgrid',
  padding: theme.spacing(1),
  gap: theme.spacing(1),
  transition: theme.transitions.create('padding'),
  '&[data-edge="top"]::before': {
    content: '""',
    position: 'absolute',
    pointerEvents: 'none',
    top: '-5px',
    left: 0,
    right: 0,
    height: '2px',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.main,
  },
  '&[data-edge="bottom"]::after': {
    content: '""',
    position: 'absolute',
    pointerEvents: 'none',
    bottom: '-5px',
    left: 0,
    right: 0,
    height: '2px',
    borderRadius: '1px',
    backgroundColor: theme.palette.primary.main,
  },
  '.edit-button': {
    opacity: 0,
    pointerEvents: 'none',
    transition: theme.transitions.create('opacity'),
  },
  '&[data-is-editable="true"]:hover': {
    '.edit-button': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
}));

interface Props {
  /**
   * Zero-based index of the field in its section.
   */
  index: number;

  /**
   * The item field to display.
   */
  field: ItemField;

  /**
   * The key of the item this field belongs to.
   */
  itemKey: string;

  /**
   * Whether the field can be edited.
   */
  isEditable: boolean;

  /**
   * Whether drag and drop operations are disabled.
   */
  isDisabled?: boolean;
}

/**
 * A card component that displays and manages a single item field.
 * Supports drag and drop reordering and inline field editing.
 */
export function ItemFieldCard(props: Props) {
  const dialog = useDisclosure();

  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

  useEffect(() => {
    if (containerRef.current === null) {
      return;
    }

    if (handleRef.current === null) {
      return;
    }

    const data: ItemFieldCardData = { type: 'field', index: props.index };

    const cleanupDraggable = draggable({
      element: containerRef.current,
      dragHandle: handleRef.current,
      canDrag: () => !props.isDisabled,
      onDrop: () => setIsDragging(false),
      onDragStart: () => setIsDragging(true),
      getInitialData: () => data,
    });

    const cleanupDropTarget = dropTargetForElements({
      element: containerRef.current,
      getIsSticky: () => true,
      getData: ({ input, element }) => {
        return attachClosestEdge(data, {
          input,
          element,
          allowedEdges: ['top', 'bottom'],
        });
      },
      onDragEnter: ({ source, self }) => {
        if (!isItemFieldCardData(source.data)) {
          return;
        }

        if (source.data.index === props.index) {
          return;
        }

        setClosestEdge(extractClosestEdge(self.data));
      },
      onDrag: ({ source, self }) => {
        if (!isItemFieldCardData(source.data)) {
          return;
        }

        if (source.data.index === props.index) {
          return;
        }

        setClosestEdge(extractClosestEdge(self.data));
      },
      onDragLeave: () => setClosestEdge(null),
      onDrop: () => setClosestEdge(null),
    });

    return () => {
      cleanupDraggable();
      cleanupDropTarget();
    };
  }, [props.index, props.isDisabled]);

  const gridRow = props.field.type === 'multiline_text' ? 'span 2' : 'span 1';

  return (
    <Container
      ref={containerRef}
      gridRow={gridRow}
      data-edge={closestEdge}
      data-is-editable={props.isEditable}
      sx={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <UpdateItemFieldDialog
        isOpen={dialog.isOpen}
        field={props.field}
        itemKey={props.itemKey}
        onClose={dialog.close}
      />
      <IconButton ref={handleRef} size="small">
        <DragIndicatorRounded fontSize="small" />
      </IconButton>
      <Typography variant="subtitle1" marginRight={2}>
        {props.field.label}
      </Typography>
      <ItemFieldTypeLabel type={props.field.type} />
      <IconButton
        size="small"
        className="edit-button"
        aria-label="Edit"
        onClick={dialog.open}
      >
        <EditRounded fontSize="small" />
      </IconButton>
    </Container>
  );
}
